import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Background.css';

const Background = ({ phase }) => {
  const canvasRef = useRef(null);
  
  // Track phase for the animation loop
  const phaseRef = useRef(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let width, height;

    // --- PHYSICS VARIABLES ---
    let currentDrift = 0; // Start at 0
    const particleCount = 1200;
    const particles = [];
    
    // Geometry
    const tubeRadius = 220;
    const tubeThickness = 70;
    const twistSpeed = 1.5;
    const rotationSpeed = 0.0008;
    
    // Colors
    const colors = [
      'rgba(0, 224, 255, ',
      'rgba(100, 200, 255, ',
      'rgba(255, 255, 255, '
    ];

    class Particle {
      constructor() {
        // If mounting during building phase, spawn at bottom
        const isBuilding = phaseRef.current === 'building-world';
        this.reset(isBuilding);
      }

      reset(spawnAtBottom = false) {
        this.strandOffset = Math.random() < 0.5 ? 0 : Math.PI;
        
        if (spawnAtBottom) {
           this.y = window.innerHeight * 1.5 + Math.random() * window.innerHeight;
        } else {
           this.y = (Math.random() - 0.5) * window.innerHeight * 3;
        }

        const theta = Math.random() * Math.PI * 2;
        const r = Math.random() * tubeThickness;
        this.offsetX = Math.cos(theta) * r;
        this.offsetZ = Math.sin(theta) * r;
        this.speedMod = 0.8 + Math.random() * 0.4;
        this.wobblePhase = Math.random() * Math.PI * 2;
        this.size = Math.random() * 1.5;
        this.colorPrefix = colors[Math.floor(Math.random() * colors.length)];
        this.alpha = 0.05 + Math.random() * 0.2;
      }

      update(time, actualDriftSpeed) {
        // Use the smoothed drift speed passed from animate()
        this.y -= actualDriftSpeed * this.speedMod;
        
        const limit = window.innerHeight * 1.5;
        
        // Loop logic
        if (this.y < -limit) {
            this.y = limit; 
        }

        // Standard Helix Calculation
        const phase = (this.y / 320) * twistSpeed;
        const angle = phase + this.strandOffset + time;
        const helixX = Math.cos(angle) * tubeRadius;
        const helixZ = Math.sin(angle) * tubeRadius;
        const wobbleX = Math.sin(time * 2 + this.wobblePhase) * 5;
        const wobbleZ = Math.cos(time * 1.5 + this.wobblePhase) * 5;

        this.x = helixX + this.offsetX + wobbleX;
        this.z = helixZ + this.offsetZ + wobbleZ;

        // Camera Tilt
        const tiltAngle = -0.35;
        const tiltedX = this.x * Math.cos(tiltAngle) - this.y * Math.sin(tiltAngle);
        const tiltedY = this.x * Math.sin(tiltAngle) + this.y * Math.cos(tiltAngle);

        this.finalX = tiltedX;
        this.finalY = tiltedY;
        this.finalZ = this.z;
        this.alpha += Math.sin(time * 5 + this.wobblePhase) * 0.002;
      }

      draw(context, centerX, centerY) {
        const fov = 350;
        const scale = fov / (fov + 400 - this.finalZ);
        const x2d = this.finalX * scale + centerX;
        const y2d = this.finalY * scale + centerY;
        const s2d = this.size * scale;

        let depthAlpha = this.alpha;
        if (this.finalZ > 100) depthAlpha *= 0.2;

        if (s2d > 0) {
          context.beginPath();
          context.arc(x2d, y2d, s2d, 0, Math.PI * 2);
          context.fillStyle = this.colorPrefix + Math.max(0, Math.min(1, depthAlpha)) + ')';
          context.fill();
        }
      }
    }

    const init = () => {
      resize();
      particles.length = 0;
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    };

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    // --- LERP UTILITY ---
    // Smoothly interpolates between start and end
    const lerp = (start, end, factor) => {
      return start + (end - start) * factor;
    };

    let time = 0;
    
    const animate = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter';

      const centerX = width / 2;
      const centerY = height / 2;
      time += rotationSpeed;

      // --- CALCULATE TARGET DRIFT ---
      let targetDrift = 0.2; // Default slow drift

      if (phaseRef.current === 'building-world') {
        targetDrift = 4.0; // Fast rise!
      } else if (phaseRef.current === 'interlude') {
        targetDrift = 0; // Paused during text
      }

      // --- SMOOTHING PHYSICS ---
      // We move 'currentDrift' towards 'targetDrift' by 5% every frame.
      // This creates a natural "braking" effect.
      currentDrift = lerp(currentDrift, targetDrift, 0.05);

      particles.forEach(p => {
        p.update(time, currentDrift);
        p.draw(ctx, centerX, centerY);
      });

      ctx.globalCompositeOperation = 'source-over';
      animationFrameId = requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resize);
    init();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // Opacity Control based on phase
  // We allow canvas to be visible during 'interlude' if you want subtle particles in background
  // Or keep strict logic:
  const showCanvas = phase === 'building-world' || phase === 'home';

  const backgroundContent = (
    <div className={`background-wrapper ${showCanvas ? 'visible' : ''}`}>
      <canvas ref={canvasRef} className="home-background-canvas" />
      <div className="cinematic-glow" />
    </div>
  );

  return createPortal(backgroundContent, document.body);
};

export default Background;